import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import Image from "next/image";
import { useEffect } from "react";
import { useSendAnalyticsEvent } from "../../analytics/events";
import { Button, StyledButton, UnstyledButton } from "../../components/Button";
import { Loading } from "../../components/Loading";
import { DEFAULT_SPACE_SIZE_PIXELS } from "../../consts";
import { useBlocksData, useSpaceInfo } from "../../liveblocks/maps";
import { styled } from "../../stitches.config";
import useKeyAction from "../../utils/useKeyAction";
import { screenshotImgUrl } from "../BigSpaceList";
import { FEATURE_TEMPLATES, Template } from "./data";
import { TemplateGallery } from "./TemplateGallery";
import { useTemplateImport } from "./useTemplateImport";

const showFeatureAtom = atomWithStorage("showFeatureTmpl", true);
const showTemplateGalleryAtom = atom(false);

export function StartTemplate() {
  const blocks = useBlocksData({ deep: true });
  const [spaceInfo, spaceInfoClient] = useSpaceInfo();
  const [showFeature, setShowFeature] = useAtom(showFeatureAtom);
  const [showGallery, setShowGallery] = useAtom(showTemplateGalleryAtom);
  const { importTemplate, importing } = useTemplateImport();
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const show = spaceInfo && !spaceInfo.starterShown;

  useEffect(() => {
    if (show && Object.keys(blocks).length > 0) {
      spaceInfoClient?.set("starterShown", true);
    }
  }, [show, blocks, spaceInfoClient]);

  useKeyAction({ key: "Escape" }, () => {
    setShowGallery(false);
  });

  if (!show) {
    return null;
  }

  function onHideClick() {
    setShowFeature(false);
  }

  function onShowAllClick() {
    sendAnalyticsEvent({ name: "showAllTemplates" });
    setShowGallery(true);
  }

  return (
    <>
      <Container>
        <H1>
          Welcome! <br /> Click anywhere to create a block, or pick a template
          from below.
        </H1>
        {showFeature ? (
          <Feature>
            <h2>Featured templates</h2>
            <HideButton onClick={onHideClick}>Hide</HideButton>
            <List>
              {FEATURE_TEMPLATES.map((t) => {
                return (
                  <TemplateListItem
                    key={t.spaceId}
                    template={t}
                    importing={importing === t.spaceId}
                    onTemplateClick={importTemplate}
                  />
                );
              })}
            </List>
          </Feature>
        ) : null}
        <Button
          color="secondary"
          style={{ marginTop: "12px" }}
          onClick={onShowAllClick}
        >
          All templates
        </Button>
      </Container>
      <TemplateGallery
        open={showGallery}
        onClose={() => setShowGallery(false)}
      />
    </>
  );
}

function TemplateListItem({
  template,
  onTemplateClick,
  importing,
}: {
  template: Template;
  onTemplateClick: (tmpl: Template) => void;
  importing?: boolean;
}) {
  const screenshot = template.screenshot
    ? template.screenshot
    : screenshotImgUrl({ id: template.spaceId });

  return (
    <ListItem importing={importing}>
      <figure>
        <LoadingContainer>{importing ? <Loading /> : null}</LoadingContainer>
        <UnstyledButton onClick={() => onTemplateClick(template)}>
          <Image
            className="tmpl-screenshot" // use classname to style as recommanded by next/image
            width={ITEM_WIDTH}
            height={ITEM_WIDTH * (3 / 5)}
            src={screenshot}
            alt={template.name}
          />
        </UnstyledButton>
        <Name>{template.name}</Name>
      </figure>
    </ListItem>
  );
}

const WIDTH = 724;

const Container = styled("div", {
  position: "absolute",
  left: 0,
  top: 0,
  willChange: "transform",
  transform: `translate(${
    0.5 * DEFAULT_SPACE_SIZE_PIXELS.width - 0.5 * WIDTH
  }px, 200px)`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const H1 = styled("h1", {
  color: "$paper60",
  lineHeight: "1.5em",
  margin: "0 auto",
  textAlign: "center",
});

const Feature = styled("div", {
  background: "$paper90",
  padding: 20,
  marginTop: 20,
  position: "relative",

  "& h2": {
    marginBottom: "$4",
    fontWeight: 500,
    color: "$paper40",
  },
});

const List = styled("ul", {
  listStyle: "none",
  display: "inline-flex",
  flexWrap: "wrap",
});

const LoadingContainer = styled("div", {
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
});

const ITEM_WIDTH = 200;

const ListItem = styled("li", {
  margin: "0 $4 $4 $4",
  width: ITEM_WIDTH,
  position: "relative",
  variants: {
    importing: {
      true: {
        [`& .tmpl-screenshot`]: {
          opacity: 0.5,
        },
      },
    },
  },
  [`& .tmpl-screenshot`]: {
    objectFit: "cover",
    border: "1px solid $paper60_20 !important", // overwrite next/Image default inlined styling
    borderRadius: 4,
    "&:hover": {
      borderColor: "$paper60",
    },
  },
  [`& .tmpl-screenshot:hover`]: {
    borderRadius: 4,
    borderColor: "$paper60 !important",
  },
});

const Name = styled("figcaption", {
  display: "block",
  color: "$paper40",
  marginTop: "$1",
});

const HideButton = styled(StyledButton, {
  fontWeight: 500,
  position: "absolute",
  right: 10,
  top: 10,
});
